import type { ContentVersionsResponse } from '@borradh-workspace/contracts';
import { contentVersionsResponseSchema } from '@borradh-workspace/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Button } from '@/components/ui/button';
import { ContentVersionSelect } from '@/features/content-studio/content-version-select';
import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';

export interface SelectedMediaVersion {
  itemId: string;
  kind: 'graphic' | 'video';
  assetId: string;
}

interface StoredSelections {
  conversationId: string | null;
  byItem: Record<string, SelectedMediaVersion>;
  latestByItem: Record<string, string>;
  activeItemId?: string;
  follow?: { itemId: string; fromDefaultAssetId: string };
}

interface MediaVersionSelectionValue {
  state: StoredSelections;
  initialise: (selection: SelectedMediaVersion) => void;
  select: (selection: SelectedMediaVersion) => void;
}

const MediaVersionSelectionContext =
  createContext<MediaVersionSelectionValue | null>(null);

const storageKey = (conversationId: string) =>
  `claire-media-version:${conversationId}`;

function readStored(conversationId?: string): StoredSelections {
  if (!conversationId)
    return {
      conversationId: null,
      byItem: Object.create(null),
      latestByItem: Object.create(null),
    };
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(storageKey(conversationId)) ?? '{}'
    ) as Partial<StoredSelections>;
    return {
      conversationId,
      byItem:
        parsed.byItem && typeof parsed.byItem === 'object'
          ? parsed.byItem
          : Object.create(null),
      latestByItem:
        parsed.latestByItem && typeof parsed.latestByItem === 'object'
          ? parsed.latestByItem
          : Object.create(null),
      activeItemId:
        typeof parsed.activeItemId === 'string'
          ? parsed.activeItemId
          : undefined,
      follow:
        parsed.follow &&
        typeof parsed.follow.itemId === 'string' &&
        typeof parsed.follow.fromDefaultAssetId === 'string'
          ? parsed.follow
          : undefined,
    };
  } catch {
    return {
      conversationId,
      byItem: Object.create(null),
      latestByItem: Object.create(null),
    };
  }
}

/** State is lifted into the chat so its next send can consume the selection. */
export function useMediaVersionSelectionState(conversationId?: string) {
  const [state, setState] = useState<StoredSelections>(() =>
    readStored(conversationId)
  );

  useEffect(() => setState(readStored(conversationId)), [conversationId]);
  useEffect(() => {
    if (!conversationId || state.conversationId !== conversationId) return;
    window.localStorage.setItem(
      storageKey(conversationId),
      JSON.stringify(state)
    );
  }, [conversationId, state]);

  const initialise = useCallback((selection: SelectedMediaVersion) => {
    setState((current) => {
      const existing = current.byItem[selection.itemId];
      const followsNewFork =
        current.follow?.itemId === selection.itemId &&
        selection.assetId !== current.follow.fromDefaultAssetId;
      // A default preview follows the saved cut through successive edits.
      // Keep an explicit history choice until the owner sends it; that send
      // arms `follow` so the resulting edit takes over instead.
      const followsLatest =
        current.activeItemId !== selection.itemId &&
        current.follow?.itemId !== selection.itemId;
      if (existing && !followsNewFork && !followsLatest) {
        if (current.latestByItem[selection.itemId] === selection.assetId)
          return current;
        return {
          ...current,
          latestByItem: {
            ...current.latestByItem,
            [selection.itemId]: selection.assetId,
          },
        };
      }
      if (
        existing?.assetId === selection.assetId &&
        current.latestByItem[selection.itemId] === selection.assetId &&
        !followsNewFork
      )
        return current;
      return {
        ...current,
        byItem: { ...current.byItem, [selection.itemId]: selection },
        latestByItem: {
          ...current.latestByItem,
          [selection.itemId]: selection.assetId,
        },
        follow: followsNewFork ? undefined : current.follow,
      };
    });
  }, []);
  const select = useCallback((selection: SelectedMediaVersion) => {
    setState((current) => ({
      ...current,
      byItem: { ...current.byItem, [selection.itemId]: selection },
      activeItemId: selection.itemId,
    }));
  }, []);
  const consumeActiveSelection = useCallback(() => {
    const active = state.activeItemId
      ? state.byItem[state.activeItemId]
      : undefined;
    if (active) {
      setState((current) => ({
        ...current,
        activeItemId: undefined,
        follow: {
          itemId: active.itemId,
          fromDefaultAssetId:
            current.latestByItem[active.itemId] ?? active.assetId,
        },
      }));
    }
    return active;
  }, [state]);

  const value = useMemo(
    () => ({ state, initialise, select }),
    [state, initialise, select]
  );
  return { value, consumeActiveSelection };
}

export function MediaVersionSelectionProvider({
  value,
  children,
}: {
  value: MediaVersionSelectionValue;
  children: ReactNode;
}) {
  return (
    <MediaVersionSelectionContext.Provider value={value}>
      {children}
    </MediaVersionSelectionContext.Provider>
  );
}

/** Explicitly open a card's render without changing what other cards show. */
export function useSelectMediaVersion() {
  return useContext(MediaVersionSelectionContext)?.select;
}

export function useSelectedMediaVersion(itemId?: string) {
  const context = useContext(MediaVersionSelectionContext);
  return itemId ? context?.state.byItem[itemId] : undefined;
}

export function useIsWaitingForMediaFork(itemId?: string) {
  const context = useContext(MediaVersionSelectionContext);
  return Boolean(itemId && context?.state.follow?.itemId === itemId);
}

export function MediaVersionSelector({
  itemId,
  kind,
  assetId,
  onSelect,
}: {
  itemId?: string;
  kind: 'graphic' | 'video';
  assetId: string;
  onSelect?: (selection: SelectedMediaVersion) => void;
}) {
  const context = useContext(MediaVersionSelectionContext);
  const history = useQuery({
    queryKey: queryKeys.content.versions(kind, assetId),
    queryFn: () =>
      apiClient.get<ContentVersionsResponse>(
        `content-batches/media/${kind}/${assetId}/versions`,
        { schema: contentVersionsResponseSchema }
      ),
    enabled: Boolean(itemId && assetId),
    staleTime: 0,
  });
  const resolvedItemId = history.data?.itemId ?? itemId;
  const defaultAssetId =
    history.data?.draft?.assetId ?? history.data?.currentAssetId ?? assetId;
  const selected = resolvedItemId
    ? context?.state.byItem[resolvedItemId]
    : undefined;
  const selectedAssetId = selected?.assetId ?? defaultAssetId;

  useEffect(() => {
    if (!resolvedItemId || !history.data) return;
    context?.initialise({
      itemId: resolvedItemId,
      kind,
      assetId: defaultAssetId,
    });
  }, [context, defaultAssetId, history.data, kind, resolvedItemId]);

  if (!itemId) return null;
  if (history.isError)
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 text-xs"
        onClick={() => void history.refetch()}
      >
        Retry versions
      </Button>
    );
  if (!history.data)
    return <div className="h-8 w-20 animate-pulse rounded-md bg-muted" />;

  const options = history.data.versions.map((version) => ({
    assetId: version.assetId,
    label: `v${version.version}`,
  }));
  if (history.data.draft) {
    options.push({ assetId: history.data.draft.assetId, label: 'Draft' });
  }

  return (
    <ContentVersionSelect
      value={selectedAssetId}
      options={options}
      onValueChange={(nextAssetId) => {
        if (!resolvedItemId) return;
        const next = { itemId: resolvedItemId, kind, assetId: nextAssetId };
        context?.select(next);
        onSelect?.(next);
      }}
    />
  );
}
